import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

import {
  GetProductTermsResponse,
  GetProductsCategories,
} from '../models/product.model';
import { TermResponse } from '../models/term.model';

import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { UtmPreservationService } from './utm-preservation.service';
import { I18nService } from './i18n.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly API_URL = '/products';
  private categories$?: Observable<GetProductsCategories>;
  private categoriesCacheKey?: string;
  private platformId = inject(PLATFORM_ID);
  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private utmPreservationService = inject(UtmPreservationService);
  private i18nService = inject(I18nService);


  getProductTermsByPlan(planUuid: string): Observable<GetProductTermsResponse> {
    const endpoint = `${this.API_URL}/terms/plan/${planUuid}`;
    return this.apiService.get<GetProductTermsResponse>(endpoint);
  }

  getCategories(): Observable<GetProductsCategories> {
    const cacheKey = this.getCategoriesCacheKey();

    // If UTM context changed (including removal), invalidate cache so we refetch once for the new context.
    if (this.categoriesCacheKey !== cacheKey) {
      this.categories$ = undefined;
      this.categoriesCacheKey = cacheKey;
    }

    if (this.categories$) {
      return this.categories$;
    }

    const endpoint = `${this.API_URL}/categories`;
    const options = this.catalogQueryOptions();
    this.categories$ = this.apiService
      .get<GetProductsCategories>(endpoint, options)
      .pipe(
      // Share a single HTTP call across all subscribers/components/guards.
      shareReplay({ bufferSize: 1, refCount: false }),
      // If the request fails, don't keep a poisoned cache; allow retry on next call.
      catchError((err) => {
        this.categories$ = undefined;
        this.categoriesCacheKey = undefined;
        return throwError(() => err);
      })
    );

    return this.categories$;
  }

  private catalogQueryOptions(): { params: HttpParams } | undefined {
    if (!this.authService.isFixaSession()) {
      return undefined;
    }
    return { params: new HttpParams().set('fixaLoggedIn', 'true') };
  }

  private getCategoriesCacheKey(): string {
    const lang = this.i18nService.getCurrentLanguageSync();
    const fixaKey = this.authService.isFixaSession() ? 'fixa:y' : 'fixa:n';

    if (!isPlatformBrowser(this.platformId)) {
      return `ssr-${lang}-${fixaKey}`;
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utmFromUrl = (urlParams.get('utm_source') || '').trim();
      if (utmFromUrl) return `utm:${utmFromUrl}-lang:${lang}-${fixaKey}`;

      const preserved = this.utmPreservationService.isUtmEnabled()
        ? (this.utmPreservationService.preservedUtmParams?.['utm_source'] || '').trim()
        : '';

      if (preserved) return `utm:${preserved}-lang:${lang}-${fixaKey}`;

      return `no-utm-lang:${lang}-${fixaKey}`;
    } catch {
      return `no-utm-lang:${lang}-${fixaKey}`;
    }
  }
  
  clearCategoriesCache(): void {
    this.categories$ = undefined;
    this.categoriesCacheKey = undefined;
  }

  getProductVariationByUuid(uuid: string): Observable<any> {
    return this.apiService.get<any>(`${this.API_URL}/variations/${uuid}`);
  }

   getProductTermByUuid(uuid: string): Observable<TermResponse> {
    return this.apiService.get<TermResponse>(`/terms${this.API_URL}/${uuid}`);
  }
}
